/**
 * Pure layout math for VirtualMasonry. Extracted from the component so it
 * can be unit-tested without React.
 *
 * The algorithm is a majority-based row-span grid with min-Y leftmost
 * packing:
 *
 *   - Two discrete cell sizes: tall = slotHeight (2 row-units),
 *     short = shortItemH (1 row-unit).
 *   - Invariant: slotHeight = 2 * shortItemH + gap, so every item's top
 *     and bottom edge lands on a regular row-unit grid.
 *   - Placement: for each item in input order, drop it into the leftmost
 *     slot-column whose next row-count is lowest. Preserves top-to-bottom
 *     / left-to-right reading order exactly.
 *
 * See .claude/scratchpad/grid-left-to-right-ordering.md for the full
 * derivation and the float-drift story that drove the integer-picker
 * design.
 */

export type CardShape = 'tall' | 'short';

export interface CellPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LayoutResult {
  cellPositions: CellPosition[];
  totalHeight: number;
  columnWidth: number;
  /**
   * Total width the cells actually occupy. Equals
   * `slotsPerRow * slotW + (slotsPerRow - 1) * masonryCellGap`.
   * Less than `width` when the snapped column count doesn't fully
   * fill the viewport — the leftover becomes empty space on the
   * right (or split left/right when `centerCells` is true). Exposed
   * so callers can size footer content to match the grid edge.
   */
  usedWidth: number;
}

export interface LayoutInput {
  cardShapes: CardShape[];
  width: number;
  minCellWidth: number;
  masonryCellGap: number;
  centerCells?: boolean;
}

export interface SlotGeometry {
  slotsPerRow: number;
  slotW: number;
  snappedColumnWidth: number;
  /** Height of a tall slot (portrait-shaped in tall-maj, 2×landscape+gap in short-maj). */
  slotHeight: number;
  /** Height of a short slot. Always `(slotHeight - gap) / 2`. */
  shortItemH: number;
  /** Vertical pitch between rows (`shortItemH + gap`). Majority-dependent. */
  rowUnit: number;
}

/**
 * Shared slot-geometry math. Given the viewport and which majority
 * the content is in, returns every derived measurement the layout and
 * skeleton generator need — column count, slot width, both cell
 * heights, and the row-unit pitch.
 *
 * `rowUnit` is majority-dependent:
 *   - short-majority: rowUnit = slotW · 9/16 + gap      (landscapeItemH + gap)
 *   - tall-majority:  rowUnit = (slotW · 16/9 + gap) / 2
 * so `isTallMajority` is a required argument. Callers that always run
 * in short-majority (e.g. the skeleton generator, whose seed pattern
 * is 80% short) pass `false`.
 */
export function computeSlotGeometry(
  width: number,
  minCellWidth: number,
  gap: number,
  isTallMajority: boolean,
): SlotGeometry {
  const uniformSpan = isTallMajority ? 3 : 4;
  const rawCount = Math.max(Math.floor(width / (minCellWidth + gap)), 1);
  const snappedColumnCount = Math.max(
    uniformSpan,
    Math.floor(rawCount / uniformSpan) * uniformSpan,
  );
  const slotsPerRow = snappedColumnCount / uniformSpan;
  const snappedColumnWidth = Math.max(
    1,
    (width - (snappedColumnCount + 1) * gap) / snappedColumnCount,
  );
  const slotW = uniformSpan * snappedColumnWidth + (uniformSpan - 1) * gap;

  const landscapeItemH = slotW * (9 / 16);
  const portraitItemH = slotW * (16 / 9);
  const slotHeight = isTallMajority ? portraitItemH : 2 * landscapeItemH + gap;
  const shortItemH = (slotHeight - gap) / 2;
  const rowUnit = shortItemH + gap;

  return { slotsPerRow, slotW, snappedColumnWidth, slotHeight, shortItemH, rowUnit };
}

export function computeMasonryLayout({
  cardShapes,
  width,
  minCellWidth,
  masonryCellGap,
  centerCells = false,
}: LayoutInput): LayoutResult {
  if (cardShapes.length === 0) {
    return { cellPositions: [], totalHeight: 0, columnWidth: minCellWidth, usedWidth: 0 };
  }

  const tallCount = cardShapes.filter((s) => s === 'tall').length;
  const isTallMajority = tallCount >= cardShapes.length - tallCount;

  const { slotsPerRow, slotW, snappedColumnWidth, slotHeight, shortItemH, rowUnit } =
    computeSlotGeometry(width, minCellWidth, masonryCellGap, isTallMajority);

  const usedWidth = slotsPerRow * slotW + (slotsPerRow - 1) * masonryCellGap;
  const centerOffset = centerCells ? Math.max(0, Math.floor((width - usedWidth) / 2)) : 0;

  // Track per-column row counts as INTEGERS (short = +1, tall = +2).
  // Do NOT switch this back to float y-values — here be dragons.
  //
  // The L→R reading-order guarantee depends on picking the *leftmost*
  // column when multiple columns tie for min next-Y. With floats, the
  // per-column sums drift by epsilon after enough additions (short adds
  // shortItemH+gap, tall adds slotHeight+gap; mathematically these are
  // commensurate row-units, but accumulated sums in different orders
  // produce different float values). Then `nextY[c] < nextY[targetCol]`
  // becomes true for columns that should be equal, a column to the right
  // beats a column to the left on the tiebreak, and items land out of
  // index order on the same row.
  //
  // Integer row counts make the comparison exact. y is derived fresh at
  // placement time as `rowCount * rowUnit`, so there's no accumulation.
  // If you need to compute y differently (e.g. variable-height rows),
  // re-derive the invariant before changing the picker.
  const columnRowCount: number[] = new Array(slotsPerRow).fill(0);
  const positions: CellPosition[] = new Array(cardShapes.length);

  for (let i = 0; i < cardShapes.length; i++) {
    const isTall = cardShapes[i] === 'tall';
    const itemH = isTall ? slotHeight : shortItemH;
    const rowsConsumed = isTall ? 2 : 1;

    let targetCol = 0;
    for (let c = 1; c < slotsPerRow; c++) {
      if (columnRowCount[c] < columnRowCount[targetCol]) targetCol = c;
    }

    const x = centerOffset + targetCol * (slotW + masonryCellGap);
    const y = columnRowCount[targetCol] * rowUnit;
    positions[i] = { x, y, width: slotW, height: itemH };
    columnRowCount[targetCol] += rowsConsumed;
  }

  let maxRowCount = 0;
  for (const r of columnRowCount) if (r > maxRowCount) maxRowCount = r;
  const totalHeight = maxRowCount > 0 ? maxRowCount * rowUnit - masonryCellGap : 0;

  return {
    cellPositions: positions,
    totalHeight,
    columnWidth: snappedColumnWidth,
    usedWidth,
  };
}
